import { useEffect, useRef, useState, type RefObject } from "react";
import { findScrollParent } from "src-next/modules/clinical-insights/helpers/findScrollParent";

const SCROLL_IDLE_MS = 100;

/**
 * Mostra o período compacto no frame só quando a barra superior saiu da viewport
 * e o scroll do `<main>` parou — evita FLIP/re-render no meio da rolagem manual.
 */
export function useFrameCompactPeriodVisibility(
  isPeriodBarVisible: boolean,
  anchorRef: RefObject<HTMLElement | null>
): boolean {
  const [showCompact, setShowCompact] = useState(false);
  const barVisibleRef = useRef(isPeriodBarVisible);
  barVisibleRef.current = isPeriodBarVisible;
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isPeriodBarVisible) {
      clearTimeout(idleTimerRef.current);
      setShowCompact(false);
      return;
    }

    const el = anchorRef.current;
    if (!el) {
      setShowCompact(true);
      return;
    }

    const root = findScrollParent(el);

    const armShowAfterIdle = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (!barVisibleRef.current) setShowCompact(true);
      }, SCROLL_IDLE_MS);
    };

    setShowCompact(false);
    armShowAfterIdle();

    const onScroll = () => {
      if (barVisibleRef.current) return;
      setShowCompact(false);
      armShowAfterIdle();
    };

    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      clearTimeout(idleTimerRef.current);
    };
  }, [isPeriodBarVisible, anchorRef]);

  return showCompact;
}
